"use client";

import React, { useEffect, useState } from "react";
import { AutoComplete, Spin } from "antd";
import { tv } from "tailwind-variants";
import { CaretDown } from "@phosphor-icons/react";
import { GooglePlaceOption } from "types/ggPlace.types";
import { Option } from "types";

const selectStyle = tv({
  base: "rounded-md !test-base",
  variants: {
    statusSelect: {
      default: "",
      disabled: "bg-[var(--bgBlackOpacity)] text-gray-400 cursor-not-allowed",
      loading: "animate-pulse",
    },
    bg: {
      default: "!text-black",
    },
    sizeSelect: {
      xs: "h-[24px]",
      xs2: "h-[28px]",
      sm: "h-[32px]",
      md: "h-[30px] xl:h-[36px] 2xl:h-[40px] w-full",
      md2: "2xl:text-[14px] 2xl:h-[38px] xl:text-[11px] xl:h-[26px] lg:text-[9px] lg:h-[21px] 2xl:leading-[20px] xl:leading-[16px] lg:leading-[12px]",
      lg: "h-[48px]",
    },
  },
  defaultVariants: {
    bg: "default",
    statusSelect: "default",
    sizeSelect: "md",
  },
});


type Props = {
  value?: string | number;
  onChange?: (value: string | number) => void;
  onSelect?: (value: string | number) => void;
  onSelectGGMap?: (value: string, option: GooglePlaceOption) => void;
  onSearch?: (keyword: string) => void | Promise<void>;
  options?: Option[];
  placeholderSelect?: string;
  loading?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
  widthSelect?: string;
  sizeSelect?: "xs" | "xs2" | "sm" | "md" | "md2" | "lg";
  statusSelect?: "default" | "disabled" | "loading";
  bg?: "default";
  variant?: "filled" | "outlined" | "borderless";
};

const AutoCompleteCommon: React.FC<Props> = ({
  value,
  onChange,
  onSelect,
  onSearch,
  options = [],
  placeholderSelect = "Nhập hoặc chọn",
  loading,
  disabled,
  allowClear = true,
  className = "",
  widthSelect,
  sizeSelect,
  statusSelect,
  bg,
  variant = "outlined",
  onSelectGGMap,
}) => {
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (value === null || value === undefined) {
      if (inputValue !== "") setInputValue("");
      return;
    }

    const matched = options.find((opt) => opt.value === value);

    if (matched) {
      if (matched.label !== inputValue) {
        setInputValue(matched.label);
      }
    } else if (typeof value === "string" || typeof value === "number") {
      if (String(value) !== inputValue) {
        setInputValue(String(value));
      }
    }
  }, [value, options, inputValue]);

  const mergedClassName = `${selectStyle({
    statusSelect,
    sizeSelect,
    bg,
  })} ${className}`;

  const handleSelect = (val: string, option: Option) => {
    setInputValue(option.label);
    onSelect?.(option.value);
    onChange?.(option.value);
    onSelectGGMap?.(val, option as GooglePlaceOption);
  };

  const handleChange = (val: string) => {
    setInputValue(val);
    const matched = options.find((opt) => opt.label === val);
    if (matched) {
      onChange?.(matched.value);
    } else {
      onChange?.(val);
    }
  };

  return (
    <AutoComplete
      value={inputValue}
      onChange={handleChange}
      onSelect={handleSelect}
      onSearch={onSearch}
      options={options}
      style={{
        width: "100%",
        minWidth: widthSelect,
      }}
      allowClear={allowClear}
      placeholder={placeholderSelect}
      className={mergedClassName}
      disabled={disabled}
      notFoundContent={
        loading ? <Spin size="small" /> : "Không có lựa chọn nào"
      }
      filterOption={(inputValue, option) =>
        (option?.label as string)
          ?.toLowerCase()
          .includes(inputValue.toLowerCase())
      }
      variant={variant}
      suffixIcon={<CaretDown size={16} />}
    />
  );
};

export default AutoCompleteCommon;
